import { Box, useTheme } from "@mui/material";
import { ApexOptions } from "apexcharts";
import { getMaskedValue } from "components/elements/MaskedModeWrapper";
import { useMUIThemeModeContext } from "context/MUIThemeModeContext";
import { useUserDataContext } from "context/UserDataContext";
import dayjs from "dayjs";
import isToday from "dayjs/plugin/isToday";
import { useAPIDashboardStrategiesCharts } from "hooks/useAPI";
import useExchangesMetadataGlobal from "hooks/useExchangesMetadataGlobal";
import ReactApexChart from "react-apexcharts";
import { useTranslation } from "react-i18next";
import { IBaseCurrencyAmountChart } from "types/types";
import { renderNumber } from "utils/formatter";

import { SMALL_CHART_MAX_SAMPLES, SUMMARY_CHART_HEIGHT} from "..";

dayjs.extend(isToday);

interface Props {
  chartData: IBaseCurrencyAmountChart;
  currency: string;
  isFullScreen?: boolean;
}

const BaseCurrencyAmountChart = ({ chartData, currency, isFullScreen }: Props) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const { isMasked, summariesSettings } = useUserDataContext();
  const { colors } = useMUIThemeModeContext();
  const { baseCurrShortDisplayedScale, baseCurrDisplayedScale } = useExchangesMetadataGlobal(currency);

  const shouldFetchFullscreen = !!isFullScreen && chartData.points.length >= SMALL_CHART_MAX_SAMPLES;
  const { data: fullscreenData } = useAPIDashboardStrategiesCharts(shouldFetchFullscreen, {
    ...summariesSettings,
    fullscreen: true,
  });

  const effectiveChartData: IBaseCurrencyAmountChart
    = shouldFetchFullscreen && fullscreenData?.chartsByCurrency[currency]?.baseCurrencyAmountChart
      ? fullscreenData.chartsByCurrency[currency].baseCurrencyAmountChart
      : chartData;

  const labels = effectiveChartData.points.map((item) =>
    Intl.DateTimeFormat("cs-CZ").format(new Date(dayjs(item.d).format()))
  );

  const series = [
    {
      name: t("summaries.charts.amount"),
      data: effectiveChartData.points.map((item) => item.a),
    },
  ];

  // chart config
  const options: ApexOptions = {
    theme: { mode: theme.palette.mode },
    tooltip: {
      followCursor: true,
      y: {
        formatter: (val: number) => getMaskedValue(isMasked, renderNumber(val, baseCurrDisplayedScale, true)),
      },
    },
    colors: [colors.black],
    chart: {
      type: "line",
      zoom: {
        enabled: false,
      },
      toolbar: {
        show: false,
      },
      background: "transparent",
    },
    dataLabels: {
      enabled: false,
    },
    stroke: {
      curve: "stepline",
      width: 3,
    },
    grid: {
      row: {
        colors: [colors.legacyGray100, "transparent"],
        opacity: 0.5,
      },
    },
    xaxis: {
      categories: labels,
      labels: {
        hideOverlappingLabels: true,
        rotate: -30,
        show: true,
      },
      tooltip: {
        enabled: false,
      },
    },
    yaxis: {
      show: true,
      min: Math.min(0, effectiveChartData.minAmount),
      labels: {
        formatter: (val: any) => getMaskedValue(isMasked, renderNumber(val, baseCurrShortDisplayedScale, true)),
      },
    },
  };

  return (
    <Box sx={{ pr: { xs: 0, md: 2 }, textAlign: "center", height: "100%" }}>
      <ReactApexChart
        options={options}
        series={series}
        type="line"
        width={"100%"}
        height={isFullScreen ? "95%" : SUMMARY_CHART_HEIGHT}
      />
    </Box>
  );
};

export default BaseCurrencyAmountChart;
